import { BrowserDiagnostic, telemetryPage } from '@myrewards/contracts';
import { isRouteErrorResponse } from 'react-router';
import { isApiError } from './api/errors';
import { recordError, type BrowserErrorType } from './feedback/diagnostics';

type Event = BrowserDiagnostic['event'];
type Sender = (input: string, init: RequestInit) => Promise<unknown>;

/** Best effort, bounded, no retries or persistence, and never part of a user's operation. */
export function createBrowserReporter(
  send: Sender = (input, init) => fetch(input, init),
  now: () => number = Date.now,
): (diagnostic: BrowserDiagnostic) => void {
  let start = now();
  const seen = new Set<string>();
  let attempts = 0;
  return (diagnostic) => {
    try {
      const parsed = BrowserDiagnostic.safeParse(diagnostic);
      if (!parsed.success) return;
      if (now() - start >= 60_000) {
        start = now();
        seen.clear();
        attempts = 0;
      }
      const { event, page, diagnostic: kind, status } = parsed.data;
      const key = JSON.stringify([event, page, kind, status]);
      if (seen.has(key) || seen.size >= 10 || (event === 'api_attempt_error' && attempts >= 4))
        return;
      if (event === 'api_attempt_error') attempts++;
      seen.add(key);
      try {
        const level =
          event === 'api_attempt_error' ||
          kind === 'access_denied' ||
          kind === 'not_found' ||
          (status !== undefined && status >= 400 && status < 500)
            ? 'warn'
            : 'error';
        console[level]('myrewards.browser', { ...parsed.data });
      } catch {
        // A broken console sink must not prevent the independent server delivery.
      }
      void send('/telemetry/browser', {
        method: 'POST',
        mode: 'same-origin',
        credentials: 'same-origin',
        redirect: 'error',
        headers: { 'content-type': 'application/json', 'x-requested-with': 'myrewards' },
        body: JSON.stringify(parsed.data),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      /* Telemetry must never prevent the page from working. */
    }
  };
}

export const reportBrowserDiagnostic = createBrowserReporter();

export function browserFailure(
  error: unknown,
  event: Event,
  path: string,
): BrowserDiagnostic | null {
  if (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error.name === 'AbortError' || error.name === 'TimeoutError')
  )
    return null;
  const status = isApiError(error) || isRouteErrorResponse(error) ? error.status : undefined;
  // Expired sessions follow the normal sign-in flow, not an application exception.
  if (status === 401) return null;
  const diagnostic =
    status === 403
      ? 'access_denied'
      : status === 404
        ? 'not_found'
        : isApiError(error) && error.code === 'invalid_response'
          ? 'invalid_response'
          : status === 0
            ? 'network_error'
            : status !== undefined
              ? 'http_error'
              : 'runtime_error';
  const requestId = isApiError(error) ? error.requestId : null;
  return {
    event,
    page: telemetryPage(path),
    diagnostic,
    ...(status !== undefined ? { status } : {}),
    ...(requestId !== null &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(requestId)
      ? { requestId }
      : {}),
  };
}

/**
 * How long a cut connection's diagnostic waits to see whether the page is going away. Leaving
 * cuts every request in flight with the same error a lost network gives: Chromium just after
 * `pagehide`, Firefox and Safari as the leave starts, up to the next page's answer before
 * `pagehide`. So a cut is reported only if the page is still here this long after. A next page
 * slower than this still logs one.
 */
export const LEAVE_HOLD_MS = 3_000;

/** The page was hidden for good (`pagehide`), until it is shown again (`pageshow`). */
let leaving = false;
const held = new Set<ReturnType<typeof setTimeout>>();

/** Sends once the hold is over, unless the page went away first. */
function unlessLeaving(work: () => void): void {
  if (leaving) return;
  const timer = setTimeout(() => {
    held.delete(timer);
    if (!leaving) work();
  }, LEAVE_HOLD_MS);
  held.add(timer);
}

const ERROR_TYPE: Record<Event, BrowserErrorType> = {
  route_error: 'route_error',
  route_not_found: 'route_error',
  window_error: 'window_error',
  unhandled_rejection: 'unhandled_rejection',
  react_error: 'react_error',
  api_error: 'api_error',
  api_attempt_error: 'api_error',
};

export function reportBrowserFailure(
  error: unknown,
  event: Event,
  path = window.location.pathname,
): void {
  try {
    const diagnostic = browserFailure(error, event, path);
    if (diagnostic === null) return;
    // The feedback report's own recent-errors ring (APP-104 §4). Kept apart from the telemetry
    // payload on purpose: nothing recorded here is ever sent by reportBrowserDiagnostic. Recorded
    // at once, so its time is right and a sign-out's clear isn't undone by a held entry.
    recordError({
      type: ERROR_TYPE[event],
      area: diagnostic.page,
      error,
      ...(diagnostic.requestId !== undefined && { requestId: diagnostic.requestId }),
    });
    // A cut connection may only be the page going away (LEAVE_HOLD_MS).
    if (diagnostic.diagnostic === 'network_error')
      unlessLeaving(() => {
        reportBrowserDiagnostic(diagnostic);
      });
    else reportBrowserDiagnostic(diagnostic);
  } catch {
    // A rejected value can be a Proxy or have throwing getters. Never replace its failure.
  }
}

/**
 * The browser's own notice that a resize observer changed the layout it watches; the rest is
 * delivered next frame. Nothing failed, so it isn't reported (APP-203).
 */
const RESIZE_OBSERVER_LOOP =
  /^ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;

export function installBrowserErrorHandlers(target: Window = window): () => void {
  const error = (e: ErrorEvent): void => {
    // APP-203: an ErrorEvent with no Error (a browser notice, another origin's script) has only
    // its message. Report that, or the feedback report's error reads blank.
    if (e.error == null && RESIZE_OBSERVER_LOOP.test(e.message)) return;
    reportBrowserFailure(
      e.error ?? (e.message === '' ? e.error : e.message),
      'window_error',
      target.location.pathname,
    );
  };
  const rejection = (e: PromiseRejectionEvent): void => {
    reportBrowserFailure(e.reason, 'unhandled_rejection', target.location.pathname);
  };
  // The page going away drops every held cut; coming back from the back/forward cache ends that.
  const hide = (): void => {
    leaving = true;
    for (const timer of held) clearTimeout(timer);
    held.clear();
  };
  const show = (): void => {
    leaving = false;
  };
  target.addEventListener('error', error);
  target.addEventListener('unhandledrejection', rejection);
  target.addEventListener('pagehide', hide);
  target.addEventListener('pageshow', show);
  return () => {
    target.removeEventListener('error', error);
    target.removeEventListener('unhandledrejection', rejection);
    target.removeEventListener('pagehide', hide);
    target.removeEventListener('pageshow', show);
    leaving = false;
    for (const timer of held) clearTimeout(timer);
    held.clear();
  };
}
